import { Button, Form, Row, Space, Table, theme } from "antd";
import { ActionTableState } from "../../action-table/action-table";
import { useCallback, useEffect, useMemo } from "react";
import { useGetEmployeeClosureByMonthsQuery as getEmployeeClosureByMonthsQuery, useGetEmployeesQuery, useGetInsurancesQuery } from "../../../../api/plamo-api";
import { Link, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import FormItem from "../../form/form-item";
import dayjs from "dayjs";

const formatHours = (workTimesSum: number) => {
  return ("00" + (workTimesSum).toFixed(2).replace(".", ",")).substr(-5);
}

function SettlementEmployeeClosure({ filter, setFilter }) {
  const selectedMonth = useMemo(() => filter.month, [filter])
  const userToken = useSelector(state => (state as any).user?.userToken);
  const [form] = Form.useForm();
  const { data: employees } = useGetEmployeesQuery({ o: 0, c: 1000, f: JSON.stringify({ externalId: { c: "NEQ", v: null } }), st: JSON.stringify({ lastName: 'asc' }) }) as any;
  const navigate = useNavigate();

  const applyPreselectedToFilter = useCallback(f => {
    const obj = JSON.parse(f);
    obj["year"] = {
      c: "EQ",
      v: selectedMonth?.year()
    }
    obj["month"] = {
      c: "EQ",
      v: selectedMonth?.month() + 1
    }
    return JSON.stringify(obj);
  }, [selectedMonth])

  const { data: employeeClosures } = getEmployeeClosureByMonthsQuery({ f: applyPreselectedToFilter('{}'), o: 0, c: 1000 }, { skip: !selectedMonth }) as any;

  const employeesWithoutClosures = useMemo(() => { 
    const employeesWithClosuresIds = employeeClosures?.items?.map(ec => ec.employeeId) || [];
    return employees?.items?.filter(e => !employeesWithClosuresIds.includes(e.id)) || [];
  }, [employees, employeeClosures])

  //console.log("Employees without closures", employeesWithoutClosures);

  const { token } = theme.useToken();
  
  const formStyle: React.CSSProperties = {
    maxWidth: 'none',
    background: token.colorFillAlter,
    borderRadius: token.borderRadiusLG,
    padding: 24,
  };

  const columns = useMemo(() => [
        {
          title: "Vorname",
          dataIndex: "firstName",
          sorter: true,
          filter: true,
          type: 'string',
          render: (value, item) => <Link to={`/employee/${item.employeeId}/settlement`}>{ value }</Link>
        },
        {
          title: "Nachname",
          dataIndex: "lastName",
          sorter: true,
          filter: true,
          type: 'string',
          render: (value, item) => <Link to={`/employee/${item.employeeId}/settlement`}>{ value }</Link>
        },
        {
          title: "Abrechnung",
          dataIndex: "employeeClosureId",
          render: (value, item) => value ? <Link to={`/employee/${item.employeeId}/settlement/closure/${item.employeeClosureId}`}>Abrechnung</Link> : <em>Keine Abrechnung erstellt!</em>,
          sorter: true,
          filter: true,
        },
        {
          title: "Stunden geleistet",
          dataIndex: "hoursSum",
          render: v => v ? formatHours(v) : ''
        },
        {
          title: "Über-/Minusstunden",
          dataIndex: "hoursRemaining",
          render: v => v ? formatHours(v) : ''
        }
      ], []);

      const generateExcel = useCallback(async () => {
          const response = await fetch(`/api/plamo/employee-closure-by-month/report/${selectedMonth.year()}/${selectedMonth.month() + 1}`, { method: 'GET', headers: { 'Accept': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', 'Authorization': `Bearer ${userToken}` } });
          const blob = await response.blob()
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement("a") as any;
          document.body.appendChild(a);
          a.style = "display: none";
          a.href = url;
          a.download = "Abrechnung.xlsx";
          a.click();
          window.URL.revokeObjectURL(url);
        }, [userToken, selectedMonth])

      const onFieldsChange = useCallback((_, allFields) => {
        const values = form.getFieldsValue()
        if(JSON.stringify(values) !== JSON.stringify(filter)) {
          setFilter(values)
        }
        console.log("Fields changed", values)
      }, [form, filter, setFilter])
      
      useEffect(() => {
        form.setFieldsValue(filter);
      }, [form, filter])
    
      return (
        <>
        <Form form={form} style={formStyle} onFieldsChange={onFieldsChange}>
          <Row><h3>Filter</h3></Row>
          <Row gutter={24}>
            <FormItem fieldName="month" label="Monat" type="month" />
            <div style={{display: "none"}}>
              <FormItem fieldName="employeeIds" label="Mitarbeiter" type="ref" typeData={{ query: useGetEmployeesQuery, getId: employee => employee.id, render: employee => `${employee.firstName} ${employee.lastName}`, multiple: true }} />
              <FormItem fieldName="insuranceIds" label="Krankenkasse(n)" type="ref" typeData={{ query: useGetInsurancesQuery, getId: insurance => insurance.id, render: insurance => insurance.name, multiple: true }} />
              <FormItem fieldName="budgets" label="Budgets" type="enum" typeData={{ options: [{ value: 'supportAmount', label: "EB" }, { value: 'absenceCare', label: "VHP" }, { value: "conversion", label: "U" }], multiple: true }} />
            </div>
          </Row>
        </Form>
        <div>
        { selectedMonth && 
        <>
          <div style={{ textAlign: 'right' }}>
            <Space size="small">
              <Button type="primary" onClick={() => generateExcel()}>
                Excel erstellen
              </Button>
            </Space>
          </div>
          <ActionTableState
                isParentLoading={false}
                buttonRow={{
                  buttons: [
                    /*
                    {
                      text: 'Neu',
                      buttonType: 'primary',
                      onClick: () => navigate(`/assignment/new${assignmentLinkQueryString}`)
                    },
                    */
                  ]
                }}
                tableData={{
                  defaultSortKey: 'id',
                  defaultSortDirection: 'desc',
                  columns,
                  dataSource: {
                    pageableQueryEndpoint: ({ f, ...rest }) => getEmployeeClosureByMonthsQuery({ f: applyPreselectedToFilter(f), ...rest }),
                    queryResultMapping: (result) => result?.items
                  },
                  actions: [
                    /*
                    {
                      caption: 'Details',
                      onClick: (item) => navigate(`/employee/${employeeId}/settlement/closure/${item.employeeClosureId}`)
                    },
                    */
                  ]
                }}            
              />
              </>
          }
      </div>
      { selectedMonth && <>
        <h2>Keine Abrechnung erstellt</h2>
        <Table
          columns={[
            {
              title: "Vorname", 
              dataIndex: "firstName",
              render: (value, item) => <Link to={`/employee/${item.id}/settlement`}>{ value }</Link>
            },
            {
              title: "Nachname", 
              dataIndex: "lastName",  
              render: (value, item) => <Link to={`/employee/${item.id}/settlement`}>{ value }</Link>
            },
            {
              title: "Aktion",
              render: (_, item) => <Button onClick={() => navigate(`/employee/${item.id}/settlement/closure/new?preselectedMonth=${dayjs(selectedMonth).format('YYYY-MM')}`)}>Abrechnung erstellen</Button>
            }
          ]} 
          dataSource={employeesWithoutClosures} 
          rowKey="id"
        />
      </>}
      </>
      )
}

export default SettlementEmployeeClosure;